import { action, computed, observable, makeAutoObservable } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { arrayMove } from 'react-movable';
import { TypeId } from 'src_common/common/websocket2/type';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { EventMarketsItemPropsType } from 'src/domains/sportsbook/trading/view/eventDetails/markets/eventMarketsItem/EventMarketsItem';
import { reorderMarkets } from 'src/domains/sportsbook/trading/view/eventDetails/markets/EventMarkets.state';

const createNewMarketId = (eventId: number, marketId: number | null): TypeId.MarketId | undefined => {
    if (marketId === null) {
        return undefined;
    }

    return TypeId.newMarketId(eventId, marketId);
};

export class EventMarketsItemState {
    @observable public openedGroup: boolean = false;

    public constructor(
        private readonly props: EventMarketsItemPropsType,
        private readonly appState: AppState
    ) {
        makeAutoObservable(this);
    }

    @action public onSortInnerMarkets = async ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
        targetRect: ClientRect;
    }): Promise<void> => {
        const { eventId } = this.props;
        const allGroups = this.props.eventMarketsState.marketGroupsArray;
        const currentGroupName = this.groupMarkets[0]?.groupName ?? '';
        const list = this.groupMarkets;
        const newItemsList = arrayMove(list, oldIndex, newIndex);

        const newAllGroups = [];
        for (const group of allGroups) {
            if (group.templateId === currentGroupName) {
                newAllGroups.push({
                    ...group,
                    items: newItemsList,
                });
            } else {
                newAllGroups.push(group);
            }
        }

        const indexedMarketList = reorderMarkets(newAllGroups);
        await this.appState.sdkStaff.event_reorder_markets({
            event_id: eventId.toOldId(),
            requestBody: {
                marketsOrder: indexedMarketList,
            },
        });
        await this.appState.eventStateActions.triggerUpdateEvent(eventId);
    };

    @action public toggleGroup = (): void => {
        this.openedGroup = !this.openedGroup;
    };

    public getTemplateNameById = (templateId: string): string | undefined => {
        return this.props.eventMarketsState.templateNamesById.get(templateId.toLowerCase());
    };

    @computed public get groupMarkets(): Array<{ market: EventMarketItemType; expanded: boolean; groupName: string }> {
        return this.props.group.sort((a, b) => a.market.displayOrder - b.market.displayOrder);
    }

    @computed public get isGroupActive(): boolean {
        return this.groupMarkets.filter((inner) => inner.market.active).length === this.groupMarkets.length;
    }

    @computed public get isGroupDisplay(): boolean {
        return this.groupMarkets.filter((inner) => inner.market.display).length === this.groupMarkets.length;
    }

    @computed public get getOpenedMarket(): TypeId.MarketId | undefined {
        return createNewMarketId(this.props.eventId.toOldId(), this.props.eventMarketsState.openedMarket);
    }

    @computed public get marketExpanded(): EventMarketItemType | undefined {
        return this.groupMarkets.find((inner) => inner.market.id === this.getOpenedMarket)?.market;
    }

    @computed public get isGroupExpended(): boolean {
        const openedMarket = this.getOpenedMarket;
        const marketExpanded = this.groupMarkets.find((inner) => inner.market.id === openedMarket);
        return openedMarket !== undefined && marketExpanded !== undefined;
    }

    @computed public get filter(): string {
        return this.props.eventMarketsState.filter;
    }

    @computed public get isOpenedMainMarket(): boolean {
        return (
            this.groupMarkets.length === 1 &&
            this.getOpenedMarket !== undefined &&
            this.marketExpanded !== undefined &&
            this.getOpenedMarket === this.groupMarkets[0]?.market.id
        );
    }

    @computed public get isMarketInnerGroup(): boolean {
        return this.groupMarkets.length > 1 || (this.isGroupExpended && this.getOpenedMarket === undefined);
    }
}
